import React from 'react'
import { Typography, Card, CardContent, CardActions } from '@mui/material'
import { Button, IconButton } from '@mui/material'
import { withStyles } from '@mui/styles'

import { notificationCardStyles } from './notificationCard.styles'

/**
 * @typedef {object} NotificationButton
 * @property {string} key - Clave única para el botón.
 * @property {string} text - Texto que se muestra en el botón.
 * @property {string} [color] - Color del texto del botón.
 * @property {function(): void} action - Función que se ejecuta al hacer clic en el botón.
 */

/**
 * @typedef {object} NotificationCardProps
 * @property {object} classes - Objeto con los nombres de las clases CSS inyectado por `withStyles`.
 * @property {string} date - La fecha de la notificación.
 * @property {string} title - El título de la notificación.
 * @property {string} text - El texto del cuerpo de la notificación.
 * @property {React.ComponentType} [Icon] - Componente de icono opcional que se muestra en la esquina superior derecha.
 * @property {function(): void} [iconAction] - Función que se ejecuta al hacer clic en el icono.
 * @property {NotificationButton[]} buttons - Array de botones de acción para la notificación.
 * @property {string} [extraClass] - Clase CSS adicional para personalizar el estilo de la tarjeta.
 */

/**
 * Componente de tarjeta de notificación con soporte para iconos y botones de acción.
 * 
 * Proporciona una tarjeta visual para mostrar notificaciones con:
 * - **Fecha**: Timestamp de la notificación
 * - **Título y texto**: Contenido principal de la notificación
 * - **Icono opcional**: Icono interactivo en la esquina superior derecha
 * - **Botones de acción**: Múltiples botones configurables en el footer
 * - **Estilos personalizables**: Soporte para clases CSS adicionales
 * 
 * @component
 * @example
 * const MyNotifications = () => {
 *   const buttons = [
 *     { key: 'view', text: 'Ver detalles', color: 'primary', action: () => console.log('View') },
 *     { key: 'dismiss', text: 'Descartar', color: 'default', action: () => console.log('Dismiss') },
 *   ];
 * 
 *   return (
 *     <NotificationCard
 *       date="Hace 2 horas"
 *       title="Nueva actualización disponible"
 *       text="Se ha publicado una nueva versión de la aplicación."
 *       Icon={CloseIcon}
 *       iconAction={() => console.log('Close')}
 *       buttons={buttons}
 *       extraClass="highlighted"
 *     />
 *   );
 * };
 * 
 * @param {NotificationCardProps} props - Las props del componente NotificationCard.
 */
export const NotificationCard = withStyles(notificationCardStyles)(({
  classes,
  date,
  title,
  text,
  Icon,
  iconAction,
  buttons,
  extraClass,
}) => {
  return (
    <Card
      className={
        classes.cardBox + (extraClass && classes[extraClass] ? ' ' + classes[extraClass] : '')
      }
    >
      <CardContent>
        {Icon ? (
          <IconButton onClick={iconAction} className={classes.icon}>
            <Icon />
          </IconButton>
        ) : null}
        <Typography variant="subtitle2" className={classes.date}>
          {date}
        </Typography>
        <Typography variant="h5" className={classes.title}>
          {title}
        </Typography>
        <Typography variant="body2" className={classes.text}>
          {text}
        </Typography>
      </CardContent>
      <CardActions className={classes.buttonBox}>
        {buttons.map((button) => (
          <Button onClick={button.action} key={button.key}>
            <Typography variant="body2" color={button.color}>
              {button.text}
            </Typography>
          </Button>
        ))}
      </CardActions>
    </Card>
  )
})
